import { FC } from 'react';

import { InsuranceAreaDTO } from '@/types/api-types';
import {
  BreadcrumbItemType,
  ExplorerItem,
  NormalizedFile,
} from '@/types/file-explorer-types';

import { cn } from '@/lib/utils';

import { ExplorerItemType } from '@/constants/table-columns-config';

import { AgreementView } from './AgreementView';
import { BasicTableView } from './BasicTableView';
import { MediaView } from './MediaView';

interface FileExplorerProps {
  items: ExplorerItem[];
  breadcrumbs: BreadcrumbItemType[];
  entityType: ExplorerItemType;
  setCurrentUUID: (uuid: string) => void;
  setCurrentEntityType: (type: ExplorerItemType) => void;
  onNavigate?: (params: { uuid: string; entityType: ExplorerItemType }) => void;
  onImageClick?: (uuid: string) => void;
  isSmall?: boolean;
  currentUUID?: string;
  isMoveFile?: boolean;
  searchTerm?: string;
  isLoading?: boolean;
  refetchData?: (pagination?: { pageIndex: number; pageSize: number }) => void;
  rowsPerPage: number;
  totalCount: number;
}

export const FileExplorer: FC<FileExplorerProps> = ({
  items,
  breadcrumbs,
  entityType,
  currentUUID,
  setCurrentUUID,
  setCurrentEntityType,
  onNavigate,
  onImageClick,
  isSmall,
  isMoveFile,
  searchTerm,
  isLoading,
  refetchData,
  rowsPerPage,
  totalCount,
}) => {
  const renderViewByEntityType = () => {
    switch (entityType) {
      case ExplorerItemType.FILE:
        if (isSmall) {
          const localItems = items.filter((item) =>
            item.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/),
          );
          return (
            <BasicTableView
              items={localItems}
              isSmall={isSmall}
              isMoveFile={isMoveFile}
              isLoading={isLoading}
              setCurrentUUID={setCurrentUUID}
              breadcrumbs={breadcrumbs}
              onNavigate={onNavigate}
              onImageClick={onImageClick}
              setCurrentEntityType={setCurrentEntityType}
              entityType={entityType}
              searchTerm={searchTerm}
              refetchData={refetchData}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
            />
          );
        }
        return (
          <MediaView
            items={items as NormalizedFile[]}
            isSmall={isSmall}
            isLoading={isLoading}
            breadcrumbs={breadcrumbs}
            onNavigate={onNavigate}
            setCurrentUUID={setCurrentUUID}
            setCurrentEntityType={setCurrentEntityType}
            searchTerm={searchTerm}
            agreementId={currentUUID}
            refetchData={refetchData}
          />
        );
      case ExplorerItemType.AGREEMENT:
        return (
          <AgreementView
            parentUuid={currentUUID as InsuranceAreaDTO['uuid']}
            isSmall={isSmall}
            isLoading={isLoading}
            setCurrentUUID={setCurrentUUID}
            breadcrumbs={breadcrumbs}
            onNavigate={onNavigate}
            setCurrentEntityType={setCurrentEntityType}
            entityType={entityType}
            searchTerm={searchTerm}
            isMoveFile={isMoveFile}
            refetchData={refetchData}
            rowsPerPage={rowsPerPage}
          />
        );
      default:
        return (
          <BasicTableView
            items={items}
            isSmall={isSmall}
            isLoading={isLoading}
            setCurrentUUID={setCurrentUUID}
            breadcrumbs={breadcrumbs}
            onNavigate={onNavigate}
            setCurrentEntityType={setCurrentEntityType}
            entityType={entityType}
            searchTerm={searchTerm}
            refetchData={refetchData}
            totalCount={totalCount}
            rowsPerPage={rowsPerPage}
          />
        );
    }
  };

  return (
    <div className={cn('relative flex h-full w-full')}>
      {renderViewByEntityType()}
    </div>
  );
};
